import axios from 'axios';
import { cacheService } from './cacheService';
import { User } from '../store/slices/authSlice';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    token: string;
    permissions: string[];
    roles: string[];
  };
}

export interface AuthCheckResponse {
  success?: boolean;
  data?: {
    user: User;
    permissions?: string[];
    roles?: string[];
  };
  // Direct response fields for when response is user object or has user property
  user?: User;
  permissions?: string[];
  roles?: string[];
  // Fields for when response is the user object itself
  id?: number;
  name?: string;
  email?: string;
  role?: string;
  is_active?: boolean;
}

export interface RegisterUserData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone?: string;
  address?: string;
  date_of_birth?: string;
  gender?: 'male' | 'female';
  employee_id?: string;
  license_number?: string;
  specialty?: string;
  role: string;
}

export interface UpdateProfileData {
  name: string;
  phone?: string;
  address?: string;
  date_of_birth?: string;
  gender?: 'male' | 'female';
  password?: string;
  password_confirmation?: string;
}

class AuthService {
  private axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true, // Add this to handle cookies
    });

    // Request interceptor to add auth token and handle CSRF
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // For login requests, get CSRF token first
        if (config.url?.includes('/login') && config.method?.toLowerCase() === 'post') {
          try {
            await this.getCSRFToken();
          } catch (error) {
            console.warn('Failed to get CSRF token:', error);
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.clearAuthCache();
          localStorage.removeItem('auth_token');
          window.location.href = '/auth/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get CSRF token for requests
   */
  async getCSRFToken(): Promise<void> {
    // The backend uses NestJS with JWT.
    // We don't need Laravel Sanctum's CSRF cookie.
    return Promise.resolve();
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.axiosInstance.post('/login', credentials);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Login failed. Please try again.');
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await this.axiosInstance.post('/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      this.clearAuthCache();
      localStorage.removeItem('auth_token');
    }
  }

  /**
   * Check current authentication status
   */
  async checkAuth(): Promise<AuthCheckResponse> {
    // Check cache first
    const cachedAuth = cacheService.get('auth_check');
    if (cachedAuth) {
      return cachedAuth;
    }
    
    try {
      const response = await this.axiosInstance.get('/user');
      const data = response.data;
      
      // Cache for 5 minutes
      cacheService.set('auth_check', data, 5 * 60 * 1000);
      
      return data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        this.clearAuthCache();
        localStorage.removeItem('auth_token');
      }
      throw error;
    }
  }

  /**
   * Register new user (admin only)
   */
  async register(userData: RegisterUserData): Promise<{ user: User }> {
    try {
      const response = await this.axiosInstance.post('/register', userData);
      // Clear user list cache when new user is registered
      cacheService.delete('user_list');
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        throw new Error(errorMessages.join(', '));
      }
      throw new Error('Registration failed. Please try again.');
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: UpdateProfileData): Promise<{ user: User }> {
    try {
      const response = await this.axiosInstance.put('/profile', profileData);
      const data = response.data.data;
      
      // Update cache
      cacheService.set('auth_check', {
        success: true,
        data: {
          user: data.user,
          permissions: data.user.permissions || [],
          roles: data.user.roles || []
        }
      });
      
      return data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        throw new Error(errorMessages.join(', '));
      }
      throw new Error('Profile update failed. Please try again.');
    }
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permissions: string[], requiredPermission: string): boolean {
    return permissions.includes('*') || permissions.includes(requiredPermission);
  }

  /**
   * Check if user has specific ability (deprecated - use hasPermission)
   */
  hasAbility(abilities: string[], requiredAbility: string): boolean {
    return this.hasPermission(abilities, requiredAbility);
  }

  /**
   * Check if user has any of the required roles
   */
  hasRole(userRoles: string[], requiredRoles: string[]): boolean {
    return requiredRoles.some(role => userRoles.includes(role));
  }

  /**
   * Get axios instance for other services
   */
  getAxiosInstance() {
    return this.axiosInstance;
  }

  /**
   * Set authentication token
   */
  setToken(token: string): void {
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Clear authentication-related cache
   */
  private clearAuthCache(): void {
    cacheService.delete('auth_check');
    cacheService.delete('user_list');
    cacheService.delete('user_permissions');
  }
}

export const authService = new AuthService();
export default authService;